import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { processMarkdownContent } from "@/features/evaluacion/utils/evaluacion.utils";

type Seccion = { id: string; titulo: string };

const limpiarTitulo = (texto: string) =>
  texto.replace(/[*_`#]/g, "").replace(/\s+/g, " ").trim();

const idSeccion = (titulo: string) =>
  "retro-" +
  titulo
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

const textoDe = (nodo: React.ReactNode): string => {
  if (typeof nodo === "string" || typeof nodo === "number") return String(nodo);
  if (Array.isArray(nodo)) return nodo.map(textoDe).join("");
  if (nodo && typeof nodo === "object" && "props" in nodo) {
    return textoDe((nodo as { props: { children?: React.ReactNode } }).props.children);
  }
  return "";
};

/** Títulos de nivel 1 a 3 del markdown, para el índice lateral. */
function extraerSecciones(markdown: string): Seccion[] {
  const vistos = new Set<string>();
  return markdown
    .split("\n")
    .map((linea) => /^#{1,3}\s+(.+)$/.exec(linea.trim())?.[1])
    .filter((t): t is string => !!t)
    .map(limpiarTitulo)
    .filter((titulo) => {
      if (!titulo || vistos.has(titulo)) return false;
      vistos.add(titulo);
      return true;
    })
    .map((titulo) => ({ id: idSeccion(titulo), titulo }));
}

/** Título con id para poder saltar a él desde el índice. */
const titulo = (Tag: "h1" | "h2" | "h3", clases: string) =>
  function Titulo({ children }: { children?: React.ReactNode }) {
    return (
      <Tag id={idSeccion(limpiarTitulo(textoDe(children)))} className={`scroll-mt-4 ${clases}`}>
        {children}
      </Tag>
    );
  };

const H1 = titulo("h1", "text-2xl font-bold text-[var(--foreground)] mb-4 mt-6 first:mt-0");
const H2 = titulo("h2", "text-xl font-bold text-[var(--foreground)] mb-3 mt-5 first:mt-0");
const H3 = titulo("h3", "text-lg font-bold text-[var(--foreground)] mb-2 mt-4 first:mt-0");

type Props = {
  contenido: string;
  isStreaming: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  /** Aviso sobre el contenido (p. ej. que se está viendo un intento anterior). */
  aviso?: React.ReactNode;
  /** Contenido fijo al pie del panel (p. ej. el botón flotante en móvil). */
  pie?: React.ReactNode;
};

export function RetroPanel({ contenido, isStreaming, scrollRef, aviso, pie }: Props) {
  const markdown = useMemo(() => processMarkdownContent(contenido), [contenido]);
  const secciones = useMemo(() => extraerSecciones(markdown), [markdown]);
  const [activa, setActiva] = useState<string | null>(null);
  const mostrarIndice = secciones.length >= 2 && !isStreaming;

  // Marca como activa la última sección cuyo título ya pasó el borde superior.
  useEffect(() => {
    const contenedor = scrollRef.current;
    if (!contenedor || !mostrarIndice) return;
    const actualizar = () => {
      const tope = contenedor.getBoundingClientRect().top + 24;
      let actual = secciones[0]?.id ?? null;
      for (const { id } of secciones) {
        const el = contenedor.querySelector(`#${CSS.escape(id)}`);
        if (el && el.getBoundingClientRect().top <= tope) actual = id;
      }
      setActiva(actual);
    };
    actualizar();
    contenedor.addEventListener("scroll", actualizar, { passive: true });
    return () => contenedor.removeEventListener("scroll", actualizar);
  }, [scrollRef, secciones, mostrarIndice]);

  const irA = (id: string) => {
    scrollRef.current
      ?.querySelector(`#${CSS.escape(id)}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative flex flex-1 min-h-0 bg-[var(--card-background)]">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto scrollbar-primary">
        <div className={`flex gap-8 px-5 pt-6 md:px-8 ${pie ? "pb-24" : "pb-6"}`}>
          {mostrarIndice && (
            <nav
              aria-label="Secciones de la retroalimentación"
              className="hidden xl:block w-44 flex-shrink-0"
            >
              <div className="sticky top-0 flex flex-col gap-1 border-r border-[var(--border)] pr-4">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--foreground)] opacity-50">
                  En esta retro
                </p>
                {secciones.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => irA(s.id)}
                    className={`text-left text-sm leading-snug transition-colors ${
                      activa === s.id
                        ? "font-semibold text-[var(--primary)]"
                        : "text-[var(--foreground)] opacity-60 hover:opacity-100"
                    }`}
                  >
                    {s.titulo}
                  </button>
                ))}
              </div>
            </nav>
          )}
          <article className="min-w-0 flex-1 max-w-[72ch]">
            {aviso}
            <ReactMarkdown
              children={markdown}
              remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={{
                p: ({ children }) => (
                  <p className="mb-4 text-[var(--foreground)] leading-relaxed">{children}</p>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold text-[var(--primary)]">{children}</strong>
                ),
                em: ({ children }) => (
                  <em className="italic text-[var(--foreground)]">{children}</em>
                ),
                h1: H1,
                h2: H2,
                h3: H3,
                ul: ({ children }) => (
                  <ul className="markdown-ul [word-break:normal] [overflow-wrap:normal]">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="markdown-ol [word-break:normal] [overflow-wrap:normal]">{children}</ol>
                ),
                li: ({
                  children,
                  ordered,
                  index,
                }: {
                  children?: React.ReactNode;
                  ordered?: boolean;
                  index?: number;
                }) => (
                  <li className="markdown-li text-[var(--foreground)]">
                    <span className="markdown-li-bullet">{ordered ? `${(index ?? 0) + 1}.` : "•"}</span>
                    <span className="markdown-li-content">{children}</span>
                  </li>
                ),
                blockquote: ({ children }) => (
                  <blockquote
                    className="border-l-4 pl-4 italic text-[var(--foreground)] my-4"
                    style={{ borderColor: "var(--primary)" }}
                  >
                    {children}
                  </blockquote>
                ),
              }}
            />
            {isStreaming && (
              <div className="mt-4 flex items-center gap-2 text-sm text-[var(--foreground)] opacity-70">
                <Loader2 size={16} className="animate-spin" />
                <span>Escribiendo...</span>
              </div>
            )}
          </article>
        </div>
      </div>
      {pie}
    </div>
  );
}
